import { Box, CircularProgress, IconButton, Tooltip } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import '../styles/components/_actions-sidebar.scss';
import { BackIcon, DownloadIcon } from '../utils/icons';
import LanguageSelector from './LanguageSelector';
import NotificationPreferencesButton from './NotificationPreferencesButton';

interface ActionsSidebarProps {
  courseId: string;
  chapterId?: string;
  onBack?: () => void;
  isMobile?: boolean;
  onDownloadCourseNotes?: () => void;
  downloadNotesLoading?: boolean;
}

const ActionsSidebar: React.FC<ActionsSidebarProps> = ({
  courseId,
  onBack,
  isMobile = false,
  onDownloadCourseNotes,
  downloadNotesLoading = false
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate(`/course/${courseId}`);
    }
  };

  return (
    <Box className={`actions-sidebar ${isMobile ? 'actions-sidebar--mobile' : ''}`}>
      <Tooltip title={t('actionsSidebar.backToCourse')} placement={isMobile ? 'bottom' : 'left'}>
        <IconButton
          className="actions-sidebar__button actions-sidebar__button--back"
          onClick={handleBack}
        >
          <BackIcon size={24} />
        </IconButton>
      </Tooltip>

      {!isMobile && <Box className="actions-sidebar__divider" />}

      {onDownloadCourseNotes && (
        <Tooltip
          title={t('notesExport.downloadTooltip')}
          placement={isMobile ? 'bottom' : 'left'}
        >
          <span>
            <IconButton
              className="actions-sidebar__button actions-sidebar__button--download"
              onClick={onDownloadCourseNotes}
              disabled={downloadNotesLoading}
              aria-label={t('notesExport.downloadTooltip')}
            >
              {downloadNotesLoading ? (
                <CircularProgress size={22} className="actions-sidebar__download-progress" />
              ) : (
                <DownloadIcon size={24} />
              )}
            </IconButton>
          </span>
        </Tooltip>
      )}

      {onDownloadCourseNotes && !isMobile && <Box className="actions-sidebar__divider" />}

      <LanguageSelector
        isMobile={isMobile}
        className="actions-sidebar__button actions-sidebar__button--settings"
      />
      <NotificationPreferencesButton
        courseId={courseId}
        isMobile={isMobile}
        className="actions-sidebar__button actions-sidebar__button--notifications"
      />
    </Box>
  );
};

export default ActionsSidebar;
